// pkg198 — client Billing → Plans & Autopay. Staff with view_billing can read
// autopay status and a plan. Only an owner or attorney can create, pause,
// resume, or cancel a plan, or set the retainer-replenishment threshold.
// Writes are also refused for agent accounts by the API.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { buildInstallmentSchedule } from '../utils/paymentPlanSchedule';

const F = { fontFamily: 'Arial, sans-serif' };
const money = n => '$' + (+n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = s => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-').map(Number);
  if (d.length !== 3 || !d[2]) return String(s);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d[1] - 1]} ${d[2]}, ${d[0]}`;
};
const fmtWhen = s => s ? new Date(s).toLocaleString('en-US') : '—';

const INST_STATUS = {
  scheduled: 'Scheduled',
  processing: 'Processing',
  paid: 'Paid',
  failed: 'Failed',
  exhausted: 'Failed',
  link_sent: 'Link sent',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
};
const PLAN_STATUS = { active: 'Active', paused: 'Paused', cancelled: 'Cancelled', completed: 'Completed' };
const FREQ = { weekly: 'Weekly', biweekly: 'Every two weeks', monthly: 'Monthly' };

function methodLabel(m) {
  if (!m) return 'None on file';
  const brand = m.brand || (m.type === 'bank' ? 'Bank account' : 'Card');
  const exp = m.type === 'card' && m.exp_month && m.exp_year
    ? ` · exp ${String(m.exp_month).padStart(2, '0')}/${m.exp_year}`
    : '';
  return `${brand} ••${m.last4}${exp}`;
}

function timingSentence(autopay) {
  if (!autopay) return '';
  if (autopay.timing === 'days_after_issue') {
    const n = Number(autopay.days_after_issue) || 0;
    const unit = n === 1 ? 'day' : 'days';
    return `New invoices are charged ${n} ${unit} after the issue date.`;
  }
  return 'New invoices are charged on the due date.';
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function ClientPaymentPlansPanel({ clientId }) {
  const { adminUser } = useAdminAuth();
  const role = adminUser && adminUser.role;
  const canManage = role === 'owner' || role === 'attorney';

  const [autopay, setAutopay] = useState(null);
  const [methods, setMethods] = useState([]);
  const [plans, setPlans] = useState([]);
  const [detail, setDetail] = useState(null);
  const [replenishment, setReplenishment] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ total: '', installment_amount: '', frequency: 'monthly', start_date: todayISO(), invoice_ids: [] });
  const [replenishForm, setReplenishForm] = useState({ enabled: false, threshold: '', amount: '' });

  const load = useCallback(async (keepId) => {
    const [autopayRow, methodRows, planRows, replenishRow, invoiceRows] = await Promise.all([
      adminApi.clientAutopay(clientId),
      adminApi.clientPaymentMethods(clientId),
      adminApi.listPaymentPlans({ clientId }),
      adminApi.getReplenishment(clientId),
      adminApi.getClientInvoices(clientId).catch(() => []),
    ]);
    setAutopay(autopayRow);
    setMethods(Array.isArray(methodRows) ? methodRows : []);
    setPlans(Array.isArray(planRows) ? planRows : []);
    setReplenishment(replenishRow);
    setReplenishForm({
      enabled: !!(replenishRow && replenishRow.enabled),
      threshold: replenishRow && replenishRow.threshold != null ? String(replenishRow.threshold) : '',
      amount: replenishRow && replenishRow.amount != null ? String(replenishRow.amount) : '',
    });
    const list = Array.isArray(invoiceRows) ? invoiceRows : ((invoiceRows && invoiceRows.invoices) || []);
    setInvoices(list);
    if (keepId) setDetail(await adminApi.getPaymentPlan(keepId));
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setDetail(null);
    load()
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const preview = useMemo(() => buildInstallmentSchedule({
    total: form.total,
    installmentAmount: form.installment_amount,
    frequency: form.frequency,
    startDate: form.start_date,
  }), [form.total, form.installment_amount, form.frequency, form.start_date]);

  const linkable = invoices.filter(inv => inv.status !== 'draft' && inv.status !== 'void' && inv.status !== 'voided');

  async function openPlan(id) {
    setError('');
    if (detail && detail.id === id) { setDetail(null); return; }
    try { setDetail(await adminApi.getPaymentPlan(id)); }
    catch (err) { setError(err.message); }
  }

  async function createPlan() {
    setError(''); setNotice('');
    if (preview.error) { setError(preview.error); return; }
    setBusy(true);
    try {
      const created = await adminApi.createPaymentPlan({
        client_id: clientId,
        total: Number(form.total),
        installment_amount: Number(form.installment_amount),
        frequency: form.frequency,
        start_date: form.start_date,
        invoice_ids: form.invoice_ids,
      });
      setShowForm(false);
      setForm({ total: '', installment_amount: '', frequency: 'monthly', start_date: todayISO(), invoice_ids: [] });
      setNotice('Payment plan created.');
      await load();
      setDetail(created);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function act(kind) {
    if (!detail) return;
    if (kind === 'cancel' && !window.confirm('Cancel this payment plan? Scheduled installments will not be charged.')) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const fn = kind === 'pause' ? adminApi.pausePaymentPlan : kind === 'resume' ? adminApi.resumePaymentPlan : adminApi.cancelPaymentPlan;
      const updated = await fn(detail.id);
      setDetail(updated);
      setNotice(kind === 'pause' ? 'Plan paused.' : kind === 'resume' ? 'Plan resumed.' : 'Plan cancelled.');
      await load(updated.id);
      setDetail(updated);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function saveReplenishment() {
    setError(''); setNotice('');
    const enabled = replenishForm.enabled === true;
    const threshold = replenishForm.threshold === '' ? null : Number(replenishForm.threshold);
    const amount = replenishForm.amount === '' ? null : Number(replenishForm.amount);
    if (enabled) {
      if (!(threshold >= 0)) { setError('Threshold must be zero or more.'); return; }
      if (!(amount >= 1)) { setError('Replenishment amount must be at least $1.00.'); return; }
    }
    setBusy(true);
    try {
      await adminApi.saveReplenishment(clientId, { enabled, threshold, amount });
      setNotice(enabled ? 'Retainer replenishment saved.' : 'Retainer replenishment turned off.');
      await load(detail && detail.id);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  function toggleInvoice(id) {
    setForm(prev => ({
      ...prev,
      invoice_ids: prev.invoice_ids.includes(id)
        ? prev.invoice_ids.filter(x => x !== id)
        : [...prev.invoice_ids, id],
    }));
  }

  if (loading) return <p style={F}>Loading payment plans…</p>;

  const rep = autopay && autopay.replenishment;

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="section-head">Autopay</div>
      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ ...F, fontSize: 13, marginTop: 0 }}>
          The client saves a card and turns autopay on from their portal sidebar: <strong>Payment methods &amp; autopay</strong>. On an open invoice, the same link sits next to Pay now.
        </p>
        <p style={{ ...F, fontSize: 13, margin: '0 0 12px' }}>
          Client page: <a href={`${window.location.origin}/payment-methods`}>{`${window.location.origin}/payment-methods`}</a>
        </p>
        <p style={{ ...F, fontSize: 14, marginTop: 0 }}>
          Autopay is <strong>{autopay && autopay.enabled ? 'on' : 'off'}</strong>.
        </p>
        <p style={{ ...F, fontSize: 13, margin: '0 0 6px' }}>Saved method: {methodLabel(autopay && autopay.method)}</p>
        <p style={{ ...F, fontSize: 13, margin: '0 0 6px' }}>Consent given: {fmtWhen(autopay && autopay.consent_at)}</p>
        <p style={{ ...F, fontSize: 13, margin: '0 0 6px', color: '#555' }}>{timingSentence(autopay)}</p>
        {role === 'owner' && (
          <p style={{ ...F, fontSize: 13, marginBottom: 0 }}>
            <Link to="/admin/dashboard/firm-settings">Firm Settings</Link> holds the charge schedule and the retainer-replenishment switch. This screen does not change LawPay test or live mode.
          </p>
        )}
        {methods.length > 0 && (
          <ul style={{ ...F, fontSize: 13, margin: '12px 0 0', paddingLeft: 18 }}>
            {methods.map(m => <li key={m.id}>{methodLabel(m)} <span style={{ color: '#888' }}>({m.type === 'bank' ? 'bank' : 'card'})</span></li>)}
          </ul>
        )}
      </div>

      <div className="section-head">Retainer replenishment</div>
      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ ...F, fontSize: 13, color: '#555', marginTop: 0 }}>
          A trust replenishment runs only when the firm switch and this client’s record are both on, and autopay is on with a saved card. The charge deposits to trust.
        </p>
        <p style={{ ...F, fontSize: 13 }}>
          Firm switch: <strong>{rep && rep.firm_enabled ? 'on' : 'off'}</strong>
          {' · '}Client record: <strong>{replenishment && replenishment.enabled ? 'on' : 'off'}</strong>
          {' · '}Autopay: <strong>{replenishment && replenishment.autopay_enabled ? 'on' : 'off'}</strong>
        </p>
        {replenishment && replenishment.enabled && (
          <p style={{ ...F, fontSize: 13 }}>
            Charge {money(replenishment.amount)} when trust is below {money(replenishment.threshold)}.
            {replenishment.configured_at ? ` Set ${fmtWhen(replenishment.configured_at)}.` : ''}
          </p>
        )}
        {rep && !rep.firm_enabled && (
          <p style={{ ...F, fontSize: 13, color: '#9a4b00' }}>The firm switch is off, so a replenishment will not run until an owner turns it on in Firm Settings.</p>
        )}
        {canManage ? (
          <>
            <label style={{ ...F, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={replenishForm.enabled} onChange={e => setReplenishForm(p => ({ ...p, enabled: e.target.checked }))} />
              Replenish this client’s trust automatically
            </label>
            <div className="form-row" style={{ marginTop: 10 }}>
              <div className="form-group">
                <label>When trust is below ($)</label>
                <input type="number" min="0" step="0.01" value={replenishForm.threshold} onWheel={e => e.currentTarget.blur()}
                  onChange={e => setReplenishForm(p => ({ ...p, threshold: e.target.value }))} disabled={!replenishForm.enabled} />
              </div>
              <div className="form-group">
                <label>Charge amount ($)</label>
                <input type="number" min="1" step="0.01" value={replenishForm.amount} onWheel={e => e.currentTarget.blur()}
                  onChange={e => setReplenishForm(p => ({ ...p, amount: e.target.value }))} disabled={!replenishForm.enabled} />
              </div>
            </div>
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={saveReplenishment}>Save replenishment</button>
          </>
        ) : (
          <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 0 }}>Only an owner or attorney can change replenishment or a payment plan.</p>
        )}
      </div>

      <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>Payment plans</span>
        {canManage && !showForm && <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowForm(true)}>New payment plan</button>}
      </div>

      {showForm && canManage && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="form-row">
            <div className="form-group">
              <label>Total ($)</label>
              <input type="number" min="0.01" step="0.01" value={form.total} onWheel={e => e.currentTarget.blur()}
                onChange={e => setForm(p => ({ ...p, total: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Installment amount ($)</label>
              <input type="number" min="0.01" step="0.01" value={form.installment_amount} onWheel={e => e.currentTarget.blur()}
                onChange={e => setForm(p => ({ ...p, installment_amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Frequency</label>
              <select value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value }))}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every two weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="form-group">
              <label>Start date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
          </div>
          {linkable.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ ...F, fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Link invoices (optional)</div>
              {linkable.map(inv => (
                <label key={inv.id} style={{ ...F, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <input type="checkbox" checked={form.invoice_ids.includes(inv.id)} onChange={() => toggleInvoice(inv.id)} />
                  #{inv.invoice_number} · {inv.status} · balance {money(inv.balance_due)}
                </label>
              ))}
            </div>
          )}
          <div style={{ ...F, fontSize: 13, marginBottom: 10 }}>
            <strong>Schedule</strong>
            {!form.total && !form.installment_amount ? <span> — enter a total and an installment amount.</span> : preview.error ? <span style={{ color: '#8a1c1c' }}> — {preview.error}</span> : (
              <div style={{ maxHeight: 220, overflow: 'auto', marginTop: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {preview.installments.map(row => (
                      <tr key={row.sequence}>
                        <td style={{ padding: '4px 8px' }}>{row.sequence}</td>
                        <td style={{ padding: '4px 8px' }}>{fmtDate(row.due_date)}</td>
                        <td style={{ padding: '4px 8px' }}>{money(row.amount)}</td>
                        <td style={{ padding: '4px 8px', color: '#888' }}>Scheduled</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn-primary" disabled={busy || !!preview.error} onClick={createPlan}>Create plan</button>
            <button type="button" className="btn" disabled={busy} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {plans.length === 0 ? <p style={{ ...F, fontSize: 13, color: '#666' }}>No payment plan for this client.</p> : (
        <div className="card" style={{ marginBottom: 16, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', ...F, fontSize: 13 }}>
            <thead>
              <tr>
                {['Started', 'Total', 'Installment', 'Frequency', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', color: '#777' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id}>
                  <td style={{ padding: '8px' }}>{fmtDate(p.start_date)}</td>
                  <td style={{ padding: '8px' }}>{money(p.total)}</td>
                  <td style={{ padding: '8px' }}>{money(p.installment_amount)}</td>
                  <td style={{ padding: '8px' }}>{FREQ[p.frequency] || p.frequency}</td>
                  <td style={{ padding: '8px' }}>{PLAN_STATUS[p.status] || p.status}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    <button type="button" className="btn btn-sm" onClick={() => openPlan(p.id)}>{detail && detail.id === p.id ? 'Hide' : 'View'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="card">
          <p style={{ ...F, fontSize: 14, marginTop: 0 }}>
            <strong>{PLAN_STATUS[detail.status] || detail.status}</strong>
            {' · '}{FREQ[detail.frequency] || detail.frequency}
            {' · '}{money(detail.total)} in {money(detail.installment_amount)} installments
            {detail.created_by_name ? ` · created by ${detail.created_by_name}` : ''}
          </p>
          {(detail.invoices || []).length > 0 && (
            <p style={{ ...F, fontSize: 13 }}>
              Linked invoices: {detail.invoices.map(inv => `#${inv.invoice_number}`).join(', ')}
            </p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', ...F, fontSize: 13 }}>
              <thead>
                <tr>
                  {['#', 'Date', 'Amount', 'Status', 'Attempts', 'Note'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', color: '#777' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(detail.installments || []).map(inst => (
                  <tr key={inst.id}>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{inst.sequence}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{fmtDate(inst.due_date)}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{money(inst.amount)}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{INST_STATUS[inst.status] || inst.status}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{inst.attempt_count || 0}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3', color: '#666' }}>{inst.last_error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canManage && (
            <div className="btn-row" style={{ marginTop: 12 }}>
              {detail.status === 'active' && <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act('pause')}>Pause</button>}
              {detail.status === 'paused' && <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act('resume')}>Resume</button>}
              {(detail.status === 'active' || detail.status === 'paused') && <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act('cancel')}>Cancel plan</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
